/**
 * Sequential profile-review navigation ("Next / Previous Profile").
 *
 * When a recruiter clicks a candidate row on ANY talent list page (all 13 pages share
 * CandidatesTable — gotcha #5), the table snapshots the visible page's row order +
 * where it came from into localStorage. The profile pages' ProfileSidebar then shows
 * a Prev/Next bar that walks that snapshot WITHOUT returning to the list.
 *
 * localStorage (not sessionStorage) on purpose: rows open profiles via RouterLink, so
 * cmd/ctrl+click opens a NEW TAB — sessionStorage wouldn't cross it. Last click wins
 * across tabs, which is the right semantic for "the list I'm currently reviewing".
 *
 * The bar self-gates: it only renders when the CURRENT profile id is in the snapshot,
 * so profiles reached any other way (search, Slack link, matching page, referrer link)
 * never show a stale bar.
 */

export interface ReviewNavCtx {
  /** Visible page's profile ids, in on-screen order, at click time. */
  ids: string[];
  /** List URL to go back to (pathname + search). */
  source: string;
  /** Human label for the source list ("Active", "Applicants", …). */
  label: string;
  /** Global rank of ids[0] within the filtered list (0-based) — display only. */
  offset: number;
  /** Full filtered row count of the source list — display only. */
  totalCount: number;
  /** Server page the snapshot came from (1-based) — drives cross-page continue. */
  page?: number;
  ts: number;
}

const KEY = "talent_review_nav";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // stale after a day

export function saveReviewNav(ctx: Omit<ReviewNavCtx, "ts">): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...ctx, ts: Date.now() }));
  } catch {
    /* storage full/blocked — feature silently off */
  }
}

export function loadReviewNav(): ReviewNavCtx | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw) as ReviewNavCtx;
    if (!Array.isArray(ctx.ids) || !ctx.ids.length || typeof ctx.source !== "string") return null;
    if (!ctx.ts || Date.now() - ctx.ts > MAX_AGE_MS) return null;
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Cross-page "continue ticket": pressing Next/Prev at the edge of a server-paginated
 * snapshot writes this and navigates back to the source list. CandidatesTable, on
 * arriving at that exact list URL, flips to the requested page via the list's OWN
 * Redux-held filters (its normal onPageChange path), re-snapshots, and auto-opens the
 * first/last row — so "candidate #31" is one click from candidate #30's profile.
 * Short TTL: a ticket is only meaningful for the seconds of that round-trip.
 */
export interface ReviewNavContinue {
  source: string;
  page: number;
  pos: "first" | "last";
  ts: number;
}

const CONT_KEY = "talent_review_nav_continue";
const CONT_TTL_MS = 45_000;

export function requestReviewNavContinue(c: Omit<ReviewNavContinue, "ts">): void {
  try {
    localStorage.setItem(CONT_KEY, JSON.stringify({ ...c, ts: Date.now() }));
  } catch {
    /* feature silently off */
  }
}

/** Peek (no delete on match — the consumer clears once acted on; mismatches are left
 *  alone so the target list can still claim the ticket; expired tickets are purged). */
export function peekReviewNavContinue(currentSource: string): ReviewNavContinue | null {
  try {
    const raw = localStorage.getItem(CONT_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as ReviewNavContinue;
    if (!c.ts || Date.now() - c.ts > CONT_TTL_MS) {
      localStorage.removeItem(CONT_KEY);
      return null;
    }
    return c.source === currentSource ? c : null;
  } catch {
    return null;
  }
}

export function clearReviewNavContinue(): void {
  try {
    localStorage.removeItem(CONT_KEY);
  } catch {
    /* noop */
  }
}

// Known list paths → labels (fallback prettifies the last path segment).
const LIST_LABELS: [string, string][] = [
  ["/talent/all", "All Candidates"],
  ["/talent/active", "Active"],
  ["/talent/passive", "Passive"],
  ["/talent/applicants/auto-routing", "Auto-Routing"],
  ["/talent/applicants/manual-review", "Manual Review"],
  ["/talent/applicants/priority-review", "Priority Manual Review"],
  ["/talent/applicants/rillet-manual-review", "Rillet Manual Review"],
  ["/talent/applicants", "Applicants"],
  ["/talent/interviews/technical", "Technical Interviews"],
  ["/talent/interviews/behavioral", "Behavioral Interviews"],
  ["/talent/onboarding", "Onboarding"],
  ["/talent/rejected/prioritization", "Rejected by Prioritization"],
  ["/talent/rejected", "Rejected"],
  ["/talent/inactive", "Inactive"],
  ["/talent/referrals", "Referrals"],
];

export function labelForListPath(pathname: string): string {
  for (const [prefix, label] of LIST_LABELS) {
    if (pathname === prefix || pathname.startsWith(prefix + "/") || pathname.startsWith(prefix + "?")) return label;
  }
  const seg = pathname.split("/").filter(Boolean).pop() || "list";
  return seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
